import { useEffect } from 'react';
import { Capacitor, registerPlugin } from '@capacitor/core';

interface MuseFlowSystemBarsPlugin {
  setHidden(options: { hidden: boolean }): Promise<void>;
}

const MuseFlowSystemBars = registerPlugin<MuseFlowSystemBarsPlugin>('MuseFlowSystemBars');

/**
 * MUS-650: the screens where the user plays. On Android the status bar and the
 * navigation bar are hidden there, so a touch near the top edge no longer pulls
 * the notification shade down over the music. A swipe from the edge still
 * brings the bars back for a moment.
 */
export const PRACTICE_ROUTE_PREFIXES = [
  '/lesson',
  '/tutorial',
  '/repertoire-play',
  '/exercises/play',
  '/exercises/note-length',
];

const matchesRoutePrefix = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

export const isPracticeRoute = (pathname: string): boolean =>
  PRACTICE_ROUTE_PREFIXES.some((prefix) => matchesRoutePrefix(pathname, prefix));

/**
 * Hide the Android system bars on a practice screen and show them everywhere
 * else. Does nothing off Android.
 */
export async function applySystemBarsForRoute(pathname: string): Promise<void> {
  if (Capacitor.getPlatform() !== 'android') return;
  await MuseFlowSystemBars.setHidden({ hidden: isPracticeRoute(pathname) });
}

/**
 * Keep the Android system bars in step with the route: App calls this with the
 * current pathname, so every route change hides or shows them.
 */
export function useSystemBarsForRoute(pathname: string): void {
  useEffect(() => {
    applySystemBarsForRoute(pathname).catch((err) => {
      console.warn('System bars error', err);
    });
  }, [pathname]);
}
